import { Injectable } from "@angular/core";
import { BehaviorSubject, debounceTime, fromEvent, merge, Subject, Subscription } from "rxjs";
import { ComponentInteractionService } from "src/app/services/component-interaction/component-interaction.service";
import { DragAndDropService } from "src/app/services/drag-and-drop/drag-and-drop.service";
import { Object3DUserData } from "@utils/shape";
import { Mesh, PerspectiveCamera, Raycaster, Scene, Vector2, Vector3, WebGLRenderer } from "three";
import { SetShapeUserDataService } from "./set-shape-userdata.service";
import { Events } from "@utils/action";
import { ExpandOrCompressShapesService } from "../../services/expand-or-compress-shapes/expand-or-compress-shapes.service";
import { TransformShapeOperations } from "../../../../../utils/shape";
import { MatDialog } from "@angular/material/dialog";
import { SceneManagerService } from "../../services/scene-manager/scene-manager.service";
import { CanvasConfig } from "../../../../../utils/canvas-configuration";
import { FaceIdentifierService } from "../face-identifier/face-identifier.service";
import { Face } from "../../../../../utils/count-calculator/count-calculator.model";
import { UndoRedoService } from "../../services/undoRedo/undo-redo.service";
import { CopyPasteService } from "../../services/drag-and-drop/copypaste.service";
import { Action, UndoRedoAction } from "~/src/app/shared/models/undoRedo.model";
import { AuxiliaryObjectType } from "../../../utils/shape-type";
import { ActivatedRoute } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { SnackBarConfig } from "../../constants/snackbar.constants";
import { Object3D, Intersection } from "three";
import { TranslateService } from "@ngx-translate/core";
import { GenericDialogComponent } from "../generic-dialog/generic-dialog.component";
import { DialogInvokingComponents } from "@models/generic-dialog.model";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { FaceMetaDataMap } from "~/src/utils/shape-facetype";
import { MeshSelection, MeshSelectionType } from "../../models/designer";
import { MeshVisualService } from "../../services/designer/mesh-visual.service";
import { HelperService } from "./helper.service";

@Injectable({
    providedIn: "root",
})

/*
  All the @hostlistners or window events are written here and this service handles
  rendering also.
*/
export class InteractionService {
    private intersects: Intersection<Object3D>[] = [];
    private draggableMeshes: Mesh[] = [];
    private renderer!: WebGLRenderer;
    private camera!: PerspectiveCamera;
    public objects!: Object3D[];
    private meshes: Mesh[] = [];
    private orbitControls!: OrbitControls;
    private isDoubleClick = false;
    private selectionList: MeshSelection[] = [];
    private subscriptions: Subscription[] = [];
    private isEdittedSubject = new Subject<boolean>();
    public isEditted$ = this.isEdittedSubject.asObservable();
    public isSelectedSubject = new BehaviorSubject<boolean>(false);
    public isSelected = this.isSelectedSubject.asObservable();
    private isMetaKeyPressed = false;
    public isGenerate = false;
    private scene!: Scene;
    private raycaster: Raycaster = new Raycaster();
    private mouse: Vector2 = new Vector2();
    private renderPending = false;
    private isFromProductDetail: boolean = false;
    private isNewSelection: boolean = false;
    private targetPosition: Vector3 | undefined;
    private lowestCellValue: number | undefined;
    private initialDuplicateOffset: Vector3 | undefined;
    private isDialogOpen: boolean = false;
    private isTragetPositionforduplicate: boolean = false;
    private isOptionShiftPressed = false;

    // Marquee selection
    private selectionStart = { x: 0, y: 0 };
    private startPoint = new Vector2();
    private endPoint = new Vector2();
    private isSelecting = false;
    private selectionBox: HTMLElement | null = null;
    private dragAction: UndoRedoAction | null = null;

    private selectedMeshes: Mesh[] = [];
    private selectedMeshesUUIDSet: Set<string> = new Set();

    constructor(
        private componentInteractionService: ComponentInteractionService,
        private dragAndDropService: DragAndDropService,
        private setShapeUserDataService: SetShapeUserDataService,
        private expandOrCompressShapesService: ExpandOrCompressShapesService,
        private dialog: MatDialog,
        private sceneManagerService: SceneManagerService,
        private faceIdentifierSrv: FaceIdentifierService,
        private copyPasteService: CopyPasteService,
        private undoRedoService: UndoRedoService,
        private route: ActivatedRoute,
        private snackBar: MatSnackBar,
        private translate: TranslateService,
        private meshVisualService: MeshVisualService,
        private helperService: HelperService,
    ) {
        this.sceneManagerService.objects$.subscribe((objects) => {
            this.objects = objects;
        });

        this.sceneManagerService.meshes$.subscribe((meshes) => {
            this.meshes = meshes;
        });

        this.componentInteractionService.getSeletedShape().subscribe((shape) => {
            if (shape && this.selectionList.length) {
                this.deselectAllMeshes();
            }
        });

        merge(this.dragAndDropService.dropOccurred$, this.undoRedoService.isActionDone$).subscribe((response) => {
            if (response) {
                this.render();
            }
        });

        this.route.queryParams.subscribe((params) => {
            this.isFromProductDetail = !!params["fromProductDetail"];
        });

        this.componentInteractionService.meshSelections$.subscribe((selections) => {
            const meshes: Mesh[] = [];
            const uuidSet = new Set<string>();

            selections.forEach((selection) => {
                meshes.push(selection.mesh);
                uuidSet.add(selection.mesh.uuid);
            });

            this.selectedMeshes = meshes;
            this.selectedMeshesUUIDSet = uuidSet;
        });
    }

    public initialize(
        scene: Scene,
        camera: PerspectiveCamera,
        renderer: WebGLRenderer,
        orbitControls: OrbitControls,
        selectionBox: HTMLElement,
    ): void {
        this.scene = scene;
        this.camera = camera;
        this.renderer = renderer;
        this.orbitControls = orbitControls;
        this.selectionBox = selectionBox;
    }

    public render(): void {
        if (!this.renderer || this.renderPending) return;

        this.renderPending = true;
        requestAnimationFrame(() => {
            if (this.renderer) {
                this.renderer.render(this.scene, this.camera);
            }
            this.renderPending = false;
        });
    }

    // Call this method wherever you modify `isEditted`
    public markAsEdited() {
        this.isEdittedSubject.next(true);
    }

    public subscribeToEvents(renderer: WebGLRenderer, camera: PerspectiveCamera): void {
        this.renderer = renderer;
        this.camera = camera;
        // We delay few miliseconds so that doubleclick and click wont conflict.
        this.subscriptions.push(
            fromEvent(this.renderer.domElement, Events.contextmenu)
                .pipe(debounceTime(150))
                .subscribe((event: Event) => {
                    if (!this.isDoubleClick) {
                        this.onContextMenu(event as MouseEvent);
                    }
                }),
        );

        this.subscriptions.push(
            fromEvent(this.renderer.domElement, Events.mousedown).subscribe((event) => {
                this.onMouseDown(event as MouseEvent);
            }),
        );

        this.subscriptions.push(
            fromEvent(this.renderer.domElement, Events.mousemove).subscribe((event) => {
                this.onMouseMove(event as MouseEvent);
            }),
        );

        this.subscriptions.push(
            fromEvent(this.renderer.domElement, Events.mouseup).subscribe((event) => {
                this.onMouseUp(event as MouseEvent);
            }),
        );

        this.subscriptions.push(
            fromEvent(this.renderer.domElement, Events.click).subscribe((event) => {
                this.onClick(event as MouseEvent);
            }),
        );

        this.subscriptions.push(
            fromEvent(document, Events.keydown).subscribe((event) => {
                this.onKeyDown(event as KeyboardEvent);
            }),
        );

        this.subscriptions.push(
            fromEvent(document, Events.keyup).subscribe((event) => {
                this.onKeyUp(event as KeyboardEvent);
            }),
        );

        this.subscriptions.push(
            fromEvent(this.renderer.domElement, Events.doubleclick).subscribe((event) => {
                this.onDoubleClick(event as MouseEvent);
            }),
        );
    }

    public getOrbitControls(): OrbitControls {
        return this.orbitControls;
    }

    public unsubscribeToEvents(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    // For selecting the shapes for drag and drop.
    private onContextMenu(event: MouseEvent): void {
        if (this.copyPasteService.copiedMeshes.length) {
            this.openSnackBar(
                this.getTranslation("designer.invalidMessage.disableDragAction"),
                SnackBarConfig.Actions.OKAY,
            );
            return;
        }
        // Ensure there are selected shapes and set them for dragging
        if (this.selectedMeshes.length > 0) {
            this.draggableMeshes = this.selectedMeshes; // Assign multiple selected shapes to drag
            this.draggableMeshes.forEach((mesh) => {
                if (Math.floor(mesh.position.y) % 10 === 5) {
                    const { row, column, cell } = this.helperService.calculateRowAndColumn(mesh);
                    mesh.userData[Object3DUserData.cellReference] = { row, column, cell };
                }
                this.setShapeUserDataService.clearAdjacentDataOnDragStart(mesh, this.meshes);
                this.dragAction = {
                    actionType: Action.DragAndDrop,
                    meshStates: this.draggableMeshes.map((draggableMesh) => ({
                        mesh: draggableMesh,
                        previousPosition: draggableMesh.position.clone(),
                    })),
                };
            });
        } else {
            this.getObjectHit(event.clientX, event.clientY);
            if (
                this.intersects.length &&
                this.intersects[0].object.name !== AuxiliaryObjectType.Plane &&
                !this.draggableMeshes.length
            ) {
                this.draggableMeshes = [this.intersects[0].object as Mesh];
                if (this.draggableMeshes[0].name === AuxiliaryObjectType.Outline) {
                    this.draggableMeshes[0] = this.draggableMeshes[0].parent as Mesh;
                }
                this.dragAction = {
                    actionType: Action.DragAndDrop,
                    meshStates: [
                        { mesh: this.draggableMeshes[0], previousPosition: this.draggableMeshes[0].position.clone() },
                    ],
                };

                this.setShapeUserDataService.clearAdjacentDataOnDragStart(this.draggableMeshes[0], this.meshes);
            }
        }
    }

    private onMouseDown(event: MouseEvent): void {
        if (!this.isOptionShiftPressed) return;
        this.isSelecting = true;
        const rect = this.renderer.domElement.getBoundingClientRect();
        this.selectionStart = { x: event.clientX - rect.left, y: event.clientY - rect.top };
        this.startPoint.set(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1
        );
    }

    // For dragging the selected shape.
    private onMouseMove(event: MouseEvent): void {
        const rect = this.renderer.domElement.getBoundingClientRect();
        // Adjust mouse coordinates to be relative to canvas position, not window
        this.mouse.set(((event.clientX - rect.left) / rect.width) * 2 - 1, -((event.clientY - rect.top) / rect.height) * 2 + 1);

        const planeMesh = this.objects.find((obj) => obj.name === AuxiliaryObjectType.Plane) as Mesh;

        const isAllDraggableMeshesSelected = this.draggableMeshes.every((mesh) =>
            this.selectedMeshesUUIDSet.has(mesh.uuid),
        );

        if (this.draggableMeshes.length && isAllDraggableMeshesSelected) {
            if (this.draggableMeshes.length === 1) {
                this.dragAndDropService.dragMesh(this.draggableMeshes[0], this.mouse, this.camera, planeMesh);
            } else if (this.draggableMeshes.length > 1) {
                this.dragAndDropService.dragMultipleMeshes(this.draggableMeshes, this.mouse, this.camera, planeMesh);
            }
            this.render();
        }

        if (this.isSelecting && this.isOptionShiftPressed) {
            const currentX = event.clientX - rect.left;
            const currentY = event.clientY - rect.top;

            const width = Math.abs(currentX - this.selectionStart.x);
            const height = Math.abs(currentY - this.selectionStart.y);

            if (width > 5 || height > 5) {

                if (this.selectionBox) {
                    this.selectionBox.style.left =
                        `${Math.min(currentX, this.selectionStart.x)}px`;

                    this.selectionBox.style.top =
                        `${Math.min(currentY, this.selectionStart.y)}px`;

                    this.selectionBox.style.width = `${width}px`;
                    this.selectionBox.style.height = `${height}px`;
                    this.selectionBox.style.display = "block";
                }
            }
        }

    }

    private onMouseUp(event: MouseEvent): void {
        if (!this.isSelecting || !this.isOptionShiftPressed) return;
        const dragDistance =
            Math.abs(event.clientX - this.selectionStart.x) + Math.abs(event.clientY - this.selectionStart.y);
        if (dragDistance < 5) {
            // If movement is too small, don't trigger selection
            this.isSelecting = false;
            if (this.selectionBox) {
                this.selectionBox.style.display = "none"; // Hide selection box
            }
            return;
        }

        const rect = this.renderer.domElement.getBoundingClientRect();

        this.endPoint.set(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1
        );
        this.hideSelectionBox();
        this.selectObjectsInBox();
    }

    private hideSelectionBox(): void {
        if (this.selectionBox) {
            this.selectionBox.style.display = "none";
            this.isSelecting = false;
        }
    }

    private selectObjectsInBox(): void {
        const selections = this.getMeshesInSelectionBox(this.startPoint, this.endPoint);
        setTimeout(() => {
            this.selectMeshes(selections);
        }, 0);
    }

    private getMeshesInSelectionBox(startPoint: Vector2, endPoint: Vector2): MeshSelection[] {
        let selectedObjects: MeshSelection[] = [];

        const meshes = this.selectionList.map((selection) => selection.mesh);

        let minX = Math.min(startPoint.x, endPoint.x);
        let maxX = Math.max(startPoint.x, endPoint.x);
        let minY = Math.min(startPoint.y, endPoint.y);
        let maxY = Math.max(startPoint.y, endPoint.y);

        const currentFaceDirection = this.componentInteractionService.getCurrentFacingDirection();

        this.meshes.forEach((mesh) => {
            if (meshes.includes(mesh)) {
                return;
            }
            let objectPosition = new Vector3(mesh.position.x, mesh.position.y, mesh.position.z);
            objectPosition.project(this.camera);

            if (
                objectPosition.x >= minX &&
                objectPosition.x <= maxX &&
                objectPosition.y >= minY &&
                objectPosition.y <= maxY
            ) {
                const meshRealFace = this.setShapeUserDataService.getMeshRealFace(mesh, currentFaceDirection);
                selectedObjects.push({
                    mesh: mesh,
                    type: MeshSelectionType.Multiple,
                    materialIndex: (mesh.userData[Object3DUserData.faceMetaDataMap] as FaceMetaDataMap)[meshRealFace]
                        .materialIndex,
                });
            }
        });

        return selectedObjects;
    }

    // For dropping the selected shape.
    private onClick(event: MouseEvent): void {
        this.renderer.domElement.focus();
        this.getObjectHit(event.clientX, event.clientY);

        if (this.isMetaKeyPressed) {
            this.componentInteractionService.setInterceptInfo(this.intersects);
        }

        if (this.draggableMeshes.length) {
            if (this.draggableMeshes.length === 1) {
                this.draggableMeshes = [this.dragAndDropService.dropMesh(this.draggableMeshes[0])];
                // Mark as edited
                this.markAsEdited();
                this.draggableMeshes = [];
            } else if (this.draggableMeshes.length > 1) {
                const planeMesh = this.objects.find((obj) => obj.name === AuxiliaryObjectType.Plane) as Mesh;
                const droppedObjects = this.dragAndDropService.dropMultipleObjects(
                    this.draggableMeshes,
                    planeMesh,
                    event,
                    this.camera,
                    this.renderer
                );

                if (!droppedObjects) {
                    return; // Prevent further execution if dropping is not allowed
                }
                this.markAsEdited();
                this.draggableMeshes = [];
            }
            this.recordMoveIfPositionChanged(true);
        }

        event.preventDefault(); // Prevent the default context menu from showing up

        if (this.isMetaKeyPressed && !event.shiftKey) {
            this.handleMetaKeyPress();
        }
    }

    private onKeyDown(event: KeyboardEvent): void {
        if (event.shiftKey && event.altKey) {
            this.isOptionShiftPressed = true;
            this.orbitControls.enabled = false;
        }
        if (event.key === Events.commandKey) {
            this.isMetaKeyPressed = true;
        }
        if (event.key === Events.escape && !this.draggableMeshes.length) {
            this.deselectAllMeshes();
        }
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "d") {
            event.preventDefault();
            this.duplicateStructure();
        }

        if (event.metaKey && event.key === "z") {
            if (!this.copyPasteService.copiedMeshes.length) {
                if (event.shiftKey) {
                    this.undoRedoService.performRedo();
                } else {
                    this.deselectAllMeshes();
                    this.undoRedoService.performUndo();
                }
                event.preventDefault();
            } else {
                this.openSnackBar(
                    this.getTranslation("designer.invalidMessage.disableUndoRedoAction"),
                    SnackBarConfig.Actions.OKAY,
                );
                return;
            }
        }

        if ((event as KeyboardEvent).key === Events.backspace || (event as KeyboardEvent).key === Events.delete) {
            if (!this.selectedMeshes.length) return;
            this.deleteMeshes(this.selectedMeshes);
            this.markAsEdited();
        }
    }

    private onKeyUp(event: KeyboardEvent): void {
        const key = (event as KeyboardEvent).key;
        const shapeToScale = this.intersects[0]?.object;
        const canScaleShape = shapeToScale && shapeToScale.name !== AuxiliaryObjectType.Plane;

        if (!event.shiftKey || !event.altKey) {
            this.isOptionShiftPressed = false;
            this.orbitControls.enabled = true;
            this.hideSelectionBox();
        }

        switch (key) {
            case Events.plusKey:
                if (canScaleShape) {
                    this.expandOrCompressShapesService.scaleShape(shapeToScale, TransformShapeOperations.expand);
                    this.render();
                }
                break;
            case Events.minusKey:
                if (canScaleShape) {
                    this.expandOrCompressShapesService.scaleShape(shapeToScale, TransformShapeOperations.compress);
                    this.render();
                }
                break;
            case Events.commandKey:
                this.isMetaKeyPressed = false;
                break;
            default:
                break;
        }
    }

    private onDoubleClick(event: MouseEvent): void {
        // This is to make the click event to wait so that it wont pick the newly generated shape.
        setTimeout(() => {
            this.isDoubleClick = false;
        }, 300);
        if (!this.dialog.openDialogs.length) {
            if (!this.draggableMeshes.length) {
                this.getObjectHit(event.clientX, event.clientY);
                if (this.intersects.length > 0) {
                    this.isGenerate = true;
                    this.componentInteractionService.setInterceptInfo(this.intersects);
                }
            }
        }
    }

    // Gives the shapes that corresponds to the mouse co-ordinates.
    private getObjectHit(x: number, y: number): void {
        const rect = this.renderer.domElement.getBoundingClientRect();
        // Adjust mouse coordinates to be relative to canvas position, not window
        this.mouse.set(((x - rect.left) / rect.width) * 2 - 1, -((y - rect.top) / rect.height) * 2 + 1);

        this.raycaster.setFromCamera(this.mouse, this.camera);
        const clickables = this.objects.filter((object) => object instanceof Mesh);
        this.intersects = this.raycaster.intersectObjects(clickables, false);

        this.componentInteractionService.setInterceptInfo(this.intersects);
    }

    private handleMetaKeyPress(): void {
        if (this.intersects.length > 0) {
            const firstIntersect = this.intersects[0];
            const materialIndex = firstIntersect.face?.materialIndex!;
            let selectedObject = firstIntersect.object as Mesh;

            if (this.isValidObject(selectedObject)) {
                this.toggleSelection(selectedObject, materialIndex);
            }
        }
    }

    private isValidObject(mesh: Mesh): boolean {
        return !!mesh.name && mesh.name !== AuxiliaryObjectType.Plane && mesh.name !== AuxiliaryObjectType.Outline;
    }

    private toggleSelection(mesh: Mesh, materialIndex: number): void {
        const isSelected = this.selectionList.some((selection) => selection.mesh.uuid === mesh.uuid);
        if (isSelected) {
            this.deselectMeshes([mesh]);
        } else {
            this.selectMeshes([{ mesh, type: MeshSelectionType.Single, materialIndex }]);
        }
    }

    public selectMeshes(selections: MeshSelection[], duplicateSelection = true): void {
        if (this.isFromProductDetail) {
            return;
        }

        this.isNewSelection = duplicateSelection;
        if (duplicateSelection) {
            this.initialDuplicateOffset = undefined;
            this.targetPosition = undefined;
            this.lowestCellValue = undefined;
        }
        this.copyPasteService.removeCopyObjects();
        this.selectionList = this.selectionList.concat(selections);
        this.componentInteractionService.setMeshSelections(this.selectionList);
        selections.forEach((selection) => {
            this.meshVisualService.setSelectionHighlight(selection.mesh);
        });
        this.isSelectedSubject.next(true);
        this.render();
    }

    private deselectMeshes(meshes: Mesh[]): void {
        meshes.forEach((mesh) => {
            this.meshVisualService.removeSelectionHighlight(mesh);
            const cell = mesh.userData[Object3DUserData.cellReference].cell;
            this.copyPasteService.removeCopyObjects();
            const index = this.selectionList.findIndex((entry) => entry.mesh.uuid === mesh.uuid);
            if (index !== -1) {
                this.selectionList.splice(index, 1);
                this.componentInteractionService.setMeshSelections(this.selectionList);
            }

            if (
                this.isTragetPositionforduplicate &&
                cell != null &&
                (this.lowestCellValue == null || cell < this.lowestCellValue)
            ) {
                this.lowestCellValue = cell;
                this.targetPosition = mesh.position.clone();
            }
        });
        this.render();
    }

    public deselectAllMeshes(isFromColorChange: boolean = false): void {
        this.selectionList.forEach((selection) => {
            this.meshVisualService.removeSelectionHighlight(selection.mesh, isFromColorChange);
        });

        // Clear the selection
        this.clearSelection();
        this.copyPasteService.removeCopyObjects();
        this.render();
    }

    private clearSelection(): void {
        this.isSelectedSubject.next(false);
        this.selectionList.length = 0;
        this.componentInteractionService.setMeshSelections([]);
    }

    public duplicateStructure(): void {
        if (this.copyPasteService.copiedMeshes.length) {
            this.openSnackBar(
                this.getTranslation("designer.invalidMessage.disableDuplicate"),
                SnackBarConfig.Actions.OKAY,
            );
            return;
        }
        this.isTragetPositionforduplicate = true;
        if (this.checkDuplicateOverlap(this.meshes)) {
            return;
        }
        let minIndex: number = -1;
        let minMesh: Mesh | undefined;
        this.selectionList.forEach(
            (
                selection: {
                    mesh: Mesh;
                    type: MeshSelectionType;
                    materialIndex: number;
                },
                index: number,
            ) => {
                const mesh = selection.mesh;
                const cell = mesh.userData?.[Object3DUserData.cellReference]?.cell;
                if (
                    cell != null &&
                    (minMesh?.userData?.[Object3DUserData.cellReference]?.cell == null ||
                        cell < minMesh.userData[Object3DUserData.cellReference].cell)
                ) {
                    minMesh = mesh;
                    minIndex = index;
                    this.isTragetPositionforduplicate = true;
                }
            },
        );
        if (!minMesh) return;
        const selectedMinCellPosition = minMesh.position.clone();
        if (!selectedMinCellPosition || !this.selectionList[minIndex]) return;
        // 🔹 Step 2: Compute offset only once per selection
        if ((this.isNewSelection || !this.initialDuplicateOffset) && this.targetPosition) {
            this.initialDuplicateOffset = new Vector3().subVectors(selectedMinCellPosition, this.targetPosition);
        }
        // 🔹 Step 3: Determine placement
        let offset = this.initialDuplicateOffset || new Vector3(0, 5, 0);
        offset.setY(0); // Prevent vertical drift unless explicitly needed
        if (this.isNewSelection) {
            // Default offset for new selection
            offset = new Vector3(0, 5, 0);
        }
        const reference = this.selectionList[minIndex].mesh.position.clone();
        const newLocation = reference.add(offset);
        // 🔹 Step 4: Duplicate and track
        const originals = [...this.selectionList.map((selection) => selection.mesh)];
        const duplicateObjects = this.copyPasteService.pasteObjectsAtNewLocation(originals, newLocation, true);
        this.deselectMeshes(originals);
        this.isTragetPositionforduplicate = false;

        const currentFaceDirection = this.componentInteractionService.getCurrentFacingDirection();
        const toBeSelectList: {
            mesh: Mesh;
            type: MeshSelectionType;
            materialIndex: number;
        }[] = [];

        duplicateObjects.forEach((mesh) => {
            const meshRealFace = this.setShapeUserDataService.getMeshRealFace(mesh, currentFaceDirection);
            toBeSelectList.push({
                mesh: mesh,
                type: MeshSelectionType.Multiple,
                materialIndex: mesh.userData[Object3DUserData.faceMetaDataMap][meshRealFace].materialIndex,
            });
        });
        this.selectMeshes(toBeSelectList, false);
    }

    // Utility method to calculate the grid index
    private calculateGridIndex(coordinate: number, cellSize: number): number {
        return coordinate / cellSize;
    }

    public checkIsGridNotAvailable(hitObject: Mesh, coordinates: Vector3): boolean {
        if (hitObject.name === AuxiliaryObjectType.Plane) {
            const cellSize = CanvasConfig.cellSize;

            // Calculate the grid row and column based on the grid coordinates
            const row = this.calculateGridIndex(coordinates.z, cellSize);
            const column = this.calculateGridIndex(coordinates.x, cellSize);

            // Create cell reference object
            const cellReference = { row, column, cell: 0 };
            // Check if the cell is occupied
            return this.isMeshInCellReference(cellReference);
        } else {
            let cellReference: { row: number; column: number; cell: number } =
                hitObject.userData[Object3DUserData.cellReference];
            let adjacentCellReference: { row: number; column: number; cell: number } | null = null;

            if (this.faceIdentifierSrv.state.frontFace) {
                adjacentCellReference = this.getAdjacentCellForFace(Face.Front, cellReference);
            } else if (this.faceIdentifierSrv.state.rightFace) {
                adjacentCellReference = this.getAdjacentCellForFace(Face.Right, cellReference);
            } else if (this.faceIdentifierSrv.state.backFace) {
                adjacentCellReference = this.getAdjacentCellForFace(Face.Back, cellReference);
            } else if (this.faceIdentifierSrv.state.leftFace) {
                adjacentCellReference = this.getAdjacentCellForFace(Face.Left, cellReference);
            } else if (this.faceIdentifierSrv.state.topFace) {
                adjacentCellReference = this.getAdjacentCellForFace(Face.Top, cellReference);
            } else if (this.faceIdentifierSrv.state.bottomFace) {
                adjacentCellReference = this.getAdjacentCellForFace(Face.Bottom, cellReference);
            }

            return !!(adjacentCellReference && this.isMeshInCellReference(adjacentCellReference));
        }
    }

    // Method to calculate adjacent cell reference based on the current face
    private getAdjacentCellForFace(face: Face, currentCellReference: { row: number; column: number; cell: number }) {
        let adjacentCellReference = { ...currentCellReference };
        switch (face) {
            case Face.Front:
                adjacentCellReference.column += 1;
                break;
            case Face.Right:
                adjacentCellReference.row -= 1;
                break;
            case Face.Back:
                adjacentCellReference.column -= 1;
                break;
            case Face.Left:
                adjacentCellReference.row += 1;
                break;
            case Face.Top:
                adjacentCellReference.cell += 1;
                break;
            case Face.Bottom:
                adjacentCellReference.cell -= 1;
                break;
        }

        return adjacentCellReference;
    }

    public isMeshInCellReference(cellReference: { row: number; column: number; cell: number }): boolean {
        return this.meshes.some(
            (mesh) =>
                mesh.userData?.[Object3DUserData.cellReference] &&
                mesh.userData[Object3DUserData.cellReference].row === cellReference.row &&
                mesh.userData[Object3DUserData.cellReference].column === cellReference.column &&
                mesh.userData[Object3DUserData.cellReference].cell === cellReference.cell,
        );
    }

    private openSnackBar(message: string, action: string): void {
        this.snackBar.open(message, action, {
            ...SnackBarConfig.Defaults,
        });
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    public deleteMeshes(meshes: Mesh[]): void {
        const selectedMeshesBeforeDelete = [...meshes];
        this.copyPasteService.removeCopyObjects();
        this.deselectAllMeshes();
        this.sceneManagerService.removeMeshesFromScene(selectedMeshesBeforeDelete);
        const action: UndoRedoAction = {
            actionType: Action.Delete,
            meshes: selectedMeshesBeforeDelete,
        };
        this.undoRedoService.addToUndo(action);
    }

    public recordMoveIfPositionChanged(dropSuccessful: boolean): void {
        if (!dropSuccessful || !this.dragAction?.meshStates?.length) {
            this.dragAction = null;
            return;
        }

        const movedMeshStates = this.dragAction.meshStates.filter(
            ({ mesh, previousPosition }) => !mesh.position.equals(previousPosition!),
        );

        if (movedMeshStates.length > 0) {
            this.undoRedoService.addToUndo({
                actionType: Action.DragAndDrop,
                meshStates: movedMeshStates,
            });
        }

        this.dragAction = null;
    }

    public checkDuplicateOverlap(allmeshesInScene: Object3D[]): boolean {
        const overlappingShapes = allmeshesInScene.filter(
            (shape): shape is Mesh => shape instanceof Mesh && Math.floor(shape.position.y) % 10 === 5,
        );
        if (overlappingShapes.length > 0) {
            this.openDialog(overlappingShapes); // pass matched shapes to dialog
            return true;
        }
        return false;
    }

    public openDialog(overlappingShapes: Mesh[]): void {
        if (this.isDialogOpen) return;
        this.isDialogOpen = true;
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: {
                componentName: DialogInvokingComponents.DuplicateStructure,
                title: this.getTranslation("genericDialogBox.duplicatestructure.title"),
                content: this.getTranslation("genericDialogBox.duplicatestructure.content"),
                firstBtn: this.getTranslation("genericDialogBox.button.deleteBtn"),
            },
            autoFocus: false,
            restoreFocus: false,
        });
        dialogRef.afterClosed().subscribe((result) => {
            this.isDialogOpen = false;
            if (result) {
                this.deleteMeshes(overlappingShapes);
            } else {
                this.deselectAllMeshes();
                const currentFaceDirection = this.componentInteractionService.getCurrentFacingDirection();
                const toBeSelectList: {
                    mesh: Mesh;
                    type: MeshSelectionType;
                    materialIndex: number;
                }[] = [];

                overlappingShapes.forEach((mesh) => {
                    const meshRealFace = this.setShapeUserDataService.getMeshRealFace(mesh, currentFaceDirection);
                    toBeSelectList.push({
                        mesh: mesh,
                        type: MeshSelectionType.Multiple,
                        materialIndex: mesh.userData[Object3DUserData.faceMetaDataMap][meshRealFace].materialIndex,
                    });
                });
                this.selectMeshes(toBeSelectList);
            }
        });
    }
}
